import { extractPingsFromText, getUsersQueryOptions, isPing, useDebouncedValue, useSelection } from '#shared'
import { useQuery } from '@tanstack/vue-query'
import { computed, reactive, ref, unref, watch, type MaybeRef } from 'vue'

export function usePingsAutocompleteController(inputEl: MaybeRef<HTMLInputElement | HTMLTextAreaElement | null>) {
  const abortController = new AbortController()

  const selection = useSelection(inputEl)
  const isInputFocused = ref(false)

  const onInputFocus = () => {
    isInputFocused.value = true
  }

  const onInputBlur = () => {
    isInputFocused.value = false
  }

  const pingPrefixSelection = computed(() => {
    const inputElVal = unref(inputEl)

    const emptySelection = { start: null, end: null }
    if (!inputElVal) return emptySelection

    const selectionVal = unref(selection)

    if (selectionVal.start === null || selectionVal.end === null) return emptySelection
    if (selectionVal.start !== selectionVal.end) return emptySelection

    const text = inputElVal.value
    const scanningSubstring = text.slice(0, selectionVal.start)
    const atIndex = scanningSubstring.lastIndexOf('@')
    if (atIndex === -1) return emptySelection

    const ping = extractPingsFromText(text.slice(atIndex))[0]
    if (!isPing(ping)) return emptySelection

    if (atIndex + ping.length < selectionVal.start) return emptySelection

    return {
      start: atIndex,
      end: atIndex + ping.length,
    }
  })

  const pingPrefix = computed(() => {
    const pingPrefixSelectionVal = pingPrefixSelection.value
    if (!pingPrefixSelectionVal) return ''
    if (pingPrefixSelectionVal.start === null || pingPrefixSelectionVal.end === null) return ''

    const text = unref(inputEl)?.value ?? ''

    // Ignoring @ symbol
    return text.slice(pingPrefixSelectionVal.start + 1, pingPrefixSelectionVal.end)
  })

  const onKeyDown = (event: Event) => {
    if (!(event instanceof KeyboardEvent)) return
    if (!pingPrefix.value) return

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      focusedAutocompleteVariantIndex.value = Math.max(focusedAutocompleteVariantIndex.value - 1, 0)
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      focusedAutocompleteVariantIndex.value = Math.min(
        focusedAutocompleteVariantIndex.value + 1,
        users.value.length - 1
      )
    }

    if (event.key === 'Tab') {
      event.preventDefault()
      onComplete(users.value[focusedAutocompleteVariantIndex.value].login)
    }
  }

  watch(
    () => unref(inputEl),
    inputElVal => {
      if (!inputElVal) return

      inputElVal.addEventListener('focus', onInputFocus, { signal: abortController.signal })
      inputElVal.addEventListener('blur', onInputBlur, { signal: abortController.signal })
      inputElVal.addEventListener('keydown', onKeyDown, { signal: abortController.signal })
    },
    { immediate: true }
  )

  const focusedAutocompleteVariantIndex = ref(-1)
  const onAutocompleteVariantFocus = (index: number) => {
    focusedAutocompleteVariantIndex.value = index
  }

  const deboundedPingPrefix = useDebouncedValue(pingPrefix, 500)

  const usersQuery = useQuery(getUsersQueryOptions(computed(() => ({ query: deboundedPingPrefix.value }))))
  const users = computed(() => usersQuery.data.value?.data ?? [])

  watch(users, () => {
    focusedAutocompleteVariantIndex.value = Math.min(focusedAutocompleteVariantIndex.value, users.value.length - 1)

    if (focusedAutocompleteVariantIndex.value === -1 && users.value.length > 0) {
      focusedAutocompleteVariantIndex.value = 0
    }
  })

  const onComplete = (ping: string) => {
    const inputElVal = unref(inputEl)
    if (!inputElVal) return

    const message = inputElVal.value

    const start = pingPrefixSelection.value.start ?? message.length
    const end = pingPrefixSelection.value.end ?? message.length

    const textToInsert = `@${ping} `

    const newMessageValue = message.slice(0, start) + textToInsert + message.slice(end)

    inputElVal.value = newMessageValue

    inputElVal.dispatchEvent(new Event('input', { bubbles: true }))

    inputElVal.focus()
    inputElVal.setSelectionRange(start + textToInsert.length, start + textToInsert.length)
  }

  return reactive({
    pingPrefix,
    selection,
    isInputFocused,
    focusedAutocompleteVariantIndex,
    onAutocompleteVariantFocus,
    onComplete,
    variants: users,
    pingPrefixSelection,
  })
}
